import { useCallback, useEffect, useState } from 'react';

import { GenericItem } from '../types';

type UseInfiniteScrollProps<TItem extends GenericItem> = {
  /** The items provided to the dropdown hook */
  items: TItem[];
  /** Whether there is another page of items to fetch */
  hasNextPage: boolean;
  /** Async function to fetch the next page of items */
  fetchNextPage: () => Promise<TItem[]>;
  /** React set state function for dropdown items */
  setItems: React.Dispatch<React.SetStateAction<TItem[]>>;
  /**
   * Object representing the last item, containing only its index.
   * If you are using @tanstack/react-virtual this is a VirtualItem
   */
  lastItem?: { index: number };
};

/**
 * A hook used to handle infinite scrolling/pagination in a dropdown
 */
export default <TItem extends GenericItem>({
  items,
  hasNextPage,
  fetchNextPage,
  setItems,
  lastItem,
}: UseInfiniteScrollProps<TItem>) => {
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);

  const fetch = useCallback(async () => {
    setIsFetchingNextPage(true);

    const newItems = await fetchNextPage();

    setItems((currentItems) => [...currentItems, ...newItems]);
    setIsFetchingNextPage(false);
  }, [fetchNextPage, setItems]);

  useEffect(() => {
    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= items.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetch();
    }
  }, [
    hasNextPage,
    fetchNextPage,
    items.length,
    lastItem,
    isFetchingNextPage,
    fetch,
  ]);
};
